import React, { forwardRef } from 'react';
import { DataTable as PrimeDataTable, DataTableValueArray, DataTableProps as PrimeDataTableProps } from 'primereact/datatable';
import Paginator from '../Paginator/Paginator';
import type { PaginatorPageChangeEvent } from '../Paginator/Paginator';
import styles from './DataTable.module.css';

/**
 * Props do componente DataTable
 */
export interface DataTableProps<T extends DataTableValueArray = any[]> extends Omit<PrimeDataTableProps<T>, 'paginator' | 'rows' | 'first' | 'onPage' | 'value' | 'selectionMode' | 'onSelectionChange' | 'selection'> {
  /**
   * Array de dados a serem exibidos
   */
  value: T;

  /**
   * Habilita paginação com o componente Paginator customizado
   * @default false
   */
  paginated?: boolean;

  /**
   * Quantidade de registros por página
   * @default 10
   */
  rows?: number;

  /**
   * Índice do primeiro registro da página atual (zero-based)
   * @default 0
   */
  first?: number;

  /**
   * Opções de quantidade de itens por página
   * @default [10, 20, 50]
   */
  rowsPerPageOptions?: number[];

  /**
   * Total de registros (necessário para paginação server-side)
   * Se não fornecido, será calculado automaticamente de value.length
   */
  totalRecords?: number;

  /**
   * Callback executado quando a página muda
   */
  onPageChange?: (event: PaginatorPageChangeEvent) => void;

  /**
   * Modo de ordenação.
   *
   * Mantém o default da biblioteca ('single') de propósito: em 'multiple' o PrimeReact
   * emite no onSort apenas `multiSortMeta`, sem `sortField`/`sortOrder`. As listagens
   * controlam a ordenação por fora (passam onSort + sortField/sortOrder) e leem esses
   * dois campos, então o modo 'multiple' deixa o clique no cabeçalho sem efeito.
   * @default "single"
   */
  sortMode?: 'single' | 'multiple';

  /**
   * Permite remover ordenação
   * @default true
   */
  removableSort?: boolean;

  /**
   * Permite redimensionar colunas
   * @default true
   */
  resizableColumns?: boolean;

  /**
   * Modo de redimensionamento de colunas
   * @default "fit"
   */
  columnResizeMode?: 'fit' | 'expand';

  /**
   * Exibe linhas de grade entre células
   * @default true
   */
  showGridlines?: boolean;

  /**
   * Tamanho da tabela
   * @default "normal"
   */
  size?: 'small' | 'normal' | 'large';

  /**
   * Estilos inline para a tabela
   */
  tableStyle?: React.CSSProperties;
}

const DataTableInner = <T extends DataTableValueArray = any[]>(
  {
    value,
    paginated = false,
    rows = 10,
    first = 0,
    rowsPerPageOptions = [10, 20, 50],
    totalRecords: totalRecordsProp,
    onPageChange,
    sortMode = 'single',
    removableSort = true,
    resizableColumns = true,
    columnResizeMode = 'fit',
    showGridlines = false,
    size = 'normal',
    tableStyle = { width: '100%' },
    className,
    children,
    ...rest
  }: DataTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
): React.ReactElement => {
  const totalRecords = totalRecordsProp ?? value?.length ?? 0;

  return (
    <div ref={ref} className={`${styles.dataTableContainer} ${className || ''}`}>
      <PrimeDataTable
        value={value}
        sortMode={sortMode}
        removableSort={removableSort}
        resizableColumns={resizableColumns}
        columnResizeMode={columnResizeMode}
        showGridlines={showGridlines}
        size={size}
        tableStyle={tableStyle}
        paginator={false}
        selectionMode={undefined}
        className={styles.dataTable}
        {...rest}
      >
        {children}
      </PrimeDataTable>

      {paginated && onPageChange && (
        <Paginator
          totalRecords={totalRecords}
          rows={rows}
          first={first}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={onPageChange}
          className={styles.paginator}
        />
      )}
    </div>
  );
};

export const DataTable = forwardRef(DataTableInner) as <T extends DataTableValueArray = any[]>(
  props: DataTableProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => React.ReactElement;

export default DataTable;
